import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    
    this.#setupFirstVideoAutoAdvance();
    this.#setupVideoSlidePlayback();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Sets up the first video to loop endlessly and restart when navigated back to.
   * Uses an IntersectionObserver on the video slide for reliable visibility detection,
   * independent of the slideshow's internal event system.
   */
  #setupFirstVideoAutoAdvance() {
    const firstVideoDeferred = this.querySelector('deferred-media[data-first-media-video]');
    if (!firstVideoDeferred) return;

    const { signal } = this.#controller;

    // The slide element that contains the video
    const videoSlide = firstVideoDeferred.closest('slideshow-slide');

    // Track whether the user has navigated away from the video slide
    let wasAway = false;

    /**
     * Restarts the video/iframe from the beginning.
     */
    const restartMedia = () => {
      /** @type {HTMLVideoElement | null} */
      const video = firstVideoDeferred.querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
        return;
      }
      /** @type {HTMLIFrameElement | null} */
      const iframe = firstVideoDeferred.querySelector('iframe[data-video-type]');
      if (iframe) {
        if (iframe.dataset.videoType === 'youtube') {
          iframe.contentWindow?.postMessage('{"event":"command","func":"seekTo","args":[0, true]}', '*');
          iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        } else if (iframe.dataset.videoType === 'vimeo') {
          iframe.contentWindow?.postMessage('{"method":"setCurrentTime","value":0}', '*');
          iframe.contentWindow?.postMessage('{"method":"play"}', '*');
        }
      }
    };

    // Use IntersectionObserver on the video slide to detect visibility changes.
    // This works regardless of slideshow event timing or initialization order.
    const scroller = videoSlide?.parentElement;
    if (videoSlide && scroller) {
      const visibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Video slide came into view — restart if user was away
              if (wasAway) {
                wasAway = false;
                restartMedia();
              }
            } else {
              // Video slide left the viewport
              wasAway = true;
            }
          }
        },
        { root: scroller, threshold: 0.5 }
      );
      visibilityObserver.observe(videoSlide);
      signal.addEventListener('abort', () => visibilityObserver.disconnect());
    }

    /**
     * Sets up loop reliability on a video element:
     * - Ensures the loop attribute is set
     * - Adds an ended event fallback to restart playback if loop fails
     */
    const setupVideoLoop = () => {
      const video = firstVideoDeferred.querySelector('video');
      if (video) {
        video.loop = true;
        // Fallback: restart manually if the ended event fires despite loop attribute
        video.addEventListener('ended', () => {
          video.currentTime = 0;
          video.play().catch(() => {});
        }, { signal });
      }
    };

    if (firstVideoDeferred.hasAttribute('data-media-loaded')) {
      setupVideoLoop();
    }

    // Watch for the video to appear in the DOM
    const mutationObserver = new MutationObserver(() => {
      if (firstVideoDeferred.querySelector('video') || firstVideoDeferred.querySelector('iframe[data-video-type]')) {
        setupVideoLoop();
        mutationObserver.disconnect();
      }
    });
    mutationObserver.observe(firstVideoDeferred, { childList: true, subtree: true });
    signal.addEventListener('abort', () => { mutationObserver.disconnect(); });
  }

  /**
   * Plays the video in the active slide and pauses videos in all other slides.
   * When slideshow autoplay is enabled, suspends the timer for the duration of
   * the video and advances to the next slide once it ends.
   * Applies to non-first deferred-media elements — the first video is handled
   * separately by #setupFirstVideoAutoAdvance.
   */
  #setupVideoSlidePlayback() {
    const { signal } = this.#controller;
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;

    slideshow.addEventListener(
      SlideshowSelectEvent.eventName,
      (/** @type {SlideshowSelectEvent} */ event) => {
        const { slide: activeSlide } = event.detail;

        // Cancel any in-progress video-end wait from a previous slide
        this.#videoEndController?.abort();
        this.#videoEndController = null;
        slideshow.removeAttribute('hold-autoplay');

        const deferredMedias = this.querySelectorAll('deferred-media:not([data-first-media-video])');

        for (const dm of deferredMedias) {
          const slide = dm.closest('slideshow-slide');
          if (!slide) continue;

          if (slide === activeSlide) {
            if (!dm.hasAttribute('data-media-loaded')) {
              dm.loadContent(false);
              dm.isPlaying = true;
              dm.updatePlayPauseHint(true);
            } else {
              // Already loaded — restart from the beginning every time
              this.#restartDeferredMedia(dm);
              dm.isPlaying = true;
              dm.updatePlayPauseHint(true);
            }

            // When autoplay is running, hold the advance until the video ends.
            // We set an attribute instead of calling suspend() because the slideshow
            // re-calls resume() internally after every transition animation, which
            // would immediately undo suspend(). The hold-autoplay attribute is checked
            // inside the interval so the timer keeps running but won't advance.
            if (slideshow.autoplay) {
              slideshow.setAttribute('hold-autoplay', '');
              this.#videoEndController = new AbortController();

              this.#waitForMediaEnd(dm, this.#videoEndController.signal, () => {
                this.#videoEndController = null;
                // Suspend first to prevent the running interval from racing with next()
                slideshow.suspend();
                slideshow.removeAttribute('hold-autoplay');
                slideshow.next();
                // Do not call resume() here — #onTransitionEnd inside the slideshow
                // calls resume() once the scroll animation completes, which is the
                // correct single point to restart the progress animation.
              });
            }
          } else if (dm.isPlaying) {
            dm.pauseMedia();
          }
        }
      },
      { signal }
    );
  }

  /** @type {AbortController | null} */
  #videoEndController = null;

  /**
   * Restarts a deferred-media element from the beginning.
   * Handles native <video>, YouTube iframes, and Vimeo iframes.
   *
   * @param {Element} dm - The deferred-media element.
   */
  #restartDeferredMedia(dm) {
    const video = dm.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.currentTime = 0;
      video.play().catch(() => {});
      return;
    }

    const iframe = dm.querySelector('iframe[data-video-type]');
    if (!(iframe instanceof HTMLIFrameElement)) return;

    if (iframe.dataset.videoType === 'youtube') {
      iframe.contentWindow?.postMessage('{"event":"command","func":"seekTo","args":[0, true]}', '*');
      iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (iframe.dataset.videoType === 'vimeo') {
      iframe.contentWindow?.postMessage('{"method":"setCurrentTime","value":0}', '*');
      iframe.contentWindow?.postMessage('{"method":"play"}', '*');
    }
  }

  /**
   * Calls callback when the media inside a deferred-media element finishes playing.
   * Supports native <video>, YouTube iframes, and Vimeo iframes.
   *
   * @param {Element} dm - The deferred-media element.
   * @param {AbortSignal} abortSignal - Aborted when the wait should be cancelled.
   * @param {() => void} callback - Invoked once when the media ends.
   */
  #waitForMediaEnd(dm, abortSignal, callback) {
    const attach = () => {
      if (abortSignal.aborted) return;

      const video = dm.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        video.addEventListener('ended', callback, { once: true, signal: abortSignal });
        return;
      }

      const iframe = dm.querySelector('iframe[data-video-type]');
      if (!(iframe instanceof HTMLIFrameElement)) return;

      const isYouTube = iframe.dataset.videoType === 'youtube';
      const isVimeo = iframe.dataset.videoType === 'vimeo';

      const onMessage = (event) => {
        if (abortSignal.aborted) return;
        try {
          const data = JSON.parse(event.data);
          if (isYouTube && data.event === 'onStateChange' && data.info === 0) callback();
          else if (isVimeo && data.event === 'ended') callback();
        } catch {}
      };

      window.addEventListener('message', onMessage, { signal: abortSignal });

      // Subscribe to player state-change events
      if (isYouTube) {
        iframe.contentWindow?.postMessage('{"event":"listening"}', '*');
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"addEventListener","args":["onStateChange"]}',
          '*'
        );
      } else if (isVimeo) {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ method: 'addEventListener', value: 'ended' }),
          '*'
        );
      }
    };

    if (dm.hasAttribute('data-media-loaded')) {
      attach();
    } else {
      // Video not yet appended — wait for loadContent to insert it
      const observer = new MutationObserver(() => {
        if (dm.querySelector('video') || dm.querySelector('iframe[data-video-type]')) {
          observer.disconnect();
          attach();
        }
      });
      observer.observe(dm, { childList: true, subtree: true });
      abortSignal.addEventListener('abort', () => observer.disconnect());
    }
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
